import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { EmpresaSidebar } from "@/components/empresa/EmpresaSidebar";
import { useCompanyAccess } from "@/hooks/useCompanyAccess";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, ArrowLeft, Save, Target, Building2, Wallet, AlertTriangle, Trash2, MessageSquare, Plus, ChevronUp, ChevronDown, X, Users, Car, GraduationCap, EyeOff, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { StateCitySelector } from "@/components/StateCitySelector";
import { formatCurrency, parseCurrency, handleCurrencyInput } from "@/lib/utils";
import { isStartPlan, canUseFeatured } from "@/lib/planUtils";

const AREAS = [
  { value: "ti", label: "TI / Tecnologia" },
  { value: "rh", label: "Recursos Humanos" },
  { value: "marketing", label: "Marketing" },
  { value: "financeiro", label: "Financeiro" },
  { value: "comercial", label: "Comercial / Vendas" },
  { value: "operacoes", label: "Operações" },
  { value: "juridico", label: "Jurídico" },
  { value: "administrativo", label: "Administrativo" },
  { value: "logistica", label: "Logística" },
  { value: "producao", label: "Produção / Industrial" },
  { value: "atendimento", label: "Atendimento ao Cliente" },
  { value: "outros", label: "Outros" },
];

const WORK_MODELS = [
  { value: "presencial", label: "Presencial" },
  { value: "híbrido", label: "Híbrido" },
  { value: "remoto", label: "Remoto" },
];

const WORK_SCHEDULES = [
  { value: "comercial", label: "Comercial (8h às 18h)" },
  { value: "turnos", label: "Turnos" },
  { value: "flexivel", label: "Flexível" },
  { value: "meio_periodo", label: "Meio período" },
];

const CONTRACT_TYPES = [
  { value: "clt", label: "CLT" },
  { value: "pj", label: "PJ" },
  { value: "estagio", label: "Estágio" },
  { value: "temporario", label: "Temporário" },
  { value: "freelancer", label: "Freelancer" },
  { value: "consultor", label: "Consultor" },
];

const BENEFITS_OPTIONS = [
  { id: "vt", label: "Vale-transporte" },
  { id: "vr", label: "Vale-refeição" },
  { id: "va", label: "Vale-alimentação" },
  { id: "plano_saude", label: "Plano de saúde" },
  { id: "plano_odonto", label: "Plano odontológico" },
  { id: "home_office", label: "Auxílio home-office" },
  { id: "educacao", label: "Auxílio educação" },
  { id: "bonus_plr", label: "Bônus / PLR" },
  { id: "gympass", label: "Gympass / Academia" },
  { id: "day_off", label: "Day Off (aniversário)" },
  { id: "outros", label: "Outros benefícios" },
];

const EDUCATION_LEVELS = [
  { value: "fundamental_incompleto", label: "Ensino Fundamental Incompleto" },
  { value: "fundamental_completo", label: "Ensino Fundamental Completo" },
  { value: "fundamental_cursando", label: "Ensino Fundamental Cursando" },
  { value: "medio_incompleto", label: "Ensino Médio Incompleto" },
  { value: "medio_completo", label: "Ensino Médio Completo" },
  { value: "medio_cursando", label: "Ensino Médio Cursando" },
  { value: "superior_incompleto", label: "Ensino Superior Incompleto" },
  { value: "superior_completo", label: "Ensino Superior Completo" },
  { value: "superior_cursando", label: "Ensino Superior Cursando" },
  { value: "pos_graduacao", label: "Pós-graduação" },
  { value: "mestrado", label: "Mestrado" },
  { value: "doutorado", label: "Doutorado" },
  { value: "indiferente", label: "Indiferente" },
];

const CNH_TYPES = [
  { value: "A", label: "Categoria A" },
  { value: "B", label: "Categoria B" },
  { value: "C", label: "Categoria C" },
  { value: "D", label: "Categoria D" },
  { value: "E", label: "Categoria E" },
];

// Empresas só podem escolher entre ativa e preenchida
// Pausada e Finalizada são gerenciados apenas pelo sistema
const STATUS_OPTIONS = [
  { value: "ativa", label: "🟢 Ativa", description: "Vaga publicada e recebendo candidaturas" },
  { value: "preenchida", label: "✅ Preenchida", description: "Vaga já foi preenchida" },
];

const QUESTION_TYPES = [
  { value: "aberta", label: "Resposta aberta" },
  { value: "multipla_escolha", label: "Múltipla escolha" },
  { value: "curta", label: "Resposta curta" },
];

interface JobQuestion {
  id: string;
  question_text: string;
  question_type: string;
  options: string[] | null;
  order_index: number;
  is_required: boolean | null;
}

const EmpresaEditarVagaPage = () => {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  const { loading: accessLoading, companyId, companyProfile } = useCompanyAccess(true);
  const [loadingJob, setLoadingJob] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Questions state
  const [questions, setQuestions] = useState<JobQuestion[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [newQuestionText, setNewQuestionText] = useState("");
  const [newQuestionType, setNewQuestionType] = useState("aberta");
  const [newOptions, setNewOptions] = useState<string[]>(["", ""]);
  const [newIsRequired, setNewIsRequired] = useState(false);
  const [savingQuestion, setSavingQuestion] = useState(false);

  // Form state - basic info
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [area, setArea] = useState("");

  // Job type flags
  const [isPcdEligible, setIsPcdEligible] = useState(false);
  const [isFirstJob, setIsFirstJob] = useState(false);
  const [isHalfPeriod, setIsHalfPeriod] = useState(false);
  const [isHomeOffice, setIsHomeOffice] = useState(false);
  const [isConfidential, setIsConfidential] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [existingIsFeatured, setExistingIsFeatured] = useState(false);

  // Experience preference
  const [experiencePreference, setExperiencePreference] = useState("indiferente");

  // Requirements and desirables (tags)
  const [requirements, setRequirements] = useState<string[]>([]);
  const [desirables, setDesirables] = useState<string[]>([]);
  const [newRequirement, setNewRequirement] = useState("");
  const [newDesirable, setNewDesirable] = useState("");

  // Education and CNH
  const [educationLevel, setEducationLevel] = useState("");
  const [cnhRequired, setCnhRequired] = useState<string[]>([]);

  // Work model
  const [jobTypes, setJobTypes] = useState<string[]>([]);
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [workSchedules, setWorkSchedules] = useState<string[]>([]);

  // Contract info
  const [contractTypes, setContractTypes] = useState<string[]>([]);
  const [salaryMin, setSalaryMin] = useState("");
  const [salaryMax, setSalaryMax] = useState("");
  const [salaryUndefined, setSalaryUndefined] = useState(false);
  const [benefits, setBenefits] = useState<string[]>([]);
  const [otherBenefits, setOtherBenefits] = useState("");
  const [status, setStatus] = useState("ativa");

  const showLocationFields = jobTypes.includes("presencial") || jobTypes.includes("híbrido");
  const isUserStartPlan = isStartPlan(companyProfile?.plan);
  const canUseFeaturedOption = canUseFeatured(companyProfile?.plan);

  // Load job data and questions
  useEffect(() => {
    if (!jobId || !companyId) return;

    const fetchJob = async () => {
      setLoadingJob(true);
      const { data, error } = await supabase
        .from("jobs")
        .select("*")
        .eq("id", jobId)
        .eq("company_id", companyId)
        .maybeSingle();

      if (error || !data) {
        toast({ title: "Erro", description: "Vaga não encontrada", variant: "destructive" });
        navigate("/empresa/dashboard");
        return;
      }

      // Populate form
      setTitle(data.title);
      setDescription(data.description);
      setArea(data.area || "");
      setJobTypes(data.job_type || []);
      setState(data.state || "");
      setCity(data.city || "");
      setNeighborhood(data.neighborhood || "");
      setWorkSchedules(data.work_schedule || []);
      setContractTypes(data.contract_type || []);
      setSalaryMin(data.salary_range_start ? formatCurrency(data.salary_range_start) : "");
      setSalaryMax(data.salary_range_end ? formatCurrency(data.salary_range_end) : "");
      setSalaryUndefined(!data.salary_range_start && !data.salary_range_end);
      setBenefits(data.benefits || []);
      setStatus(data.status || "ativa");
      
      // New fields
      setIsPcdEligible(data.is_pcd_eligible || false);
      setIsFirstJob(data.is_first_job || false);
      setIsHalfPeriod(data.is_half_period || false);
      setIsHomeOffice(data.is_home_office || false);
      setIsConfidential(data.is_confidential || false);
      setExperiencePreference(data.experience_preference || "indiferente");
      setRequirements(data.requirements || []);
      setDesirables(data.desirables || []);
      setEducationLevel(data.education_level || "");
      setCnhRequired(data.cnh_required || []);
      setOtherBenefits(data.other_benefits || "");
      setIsFeatured(data.is_featured || false);
      setExistingIsFeatured(data.is_featured || false);
      
      setLoadingJob(false);
    };

    const fetchQuestions = async () => {
      setLoadingQuestions(true);
      const { data, error } = await supabase
        .from("job_questions")
        .select("*")
        .eq("job_id", jobId)
        .order("order_index", { ascending: true });

      if (!error && data) {
        setQuestions(data);
      }
      setLoadingQuestions(false);
    };

    fetchJob();
    fetchQuestions();
  }, [jobId, companyId, navigate]);

  // Question management functions
  const handleAddQuestion = async () => {
    if (!newQuestionText.trim()) {
      toast({ title: "Erro", description: "Digite o texto da pergunta", variant: "destructive" });
      return;
    }

    if (newQuestionType === "multipla_escolha") {
      const validOptions = newOptions.filter((o) => o.trim());
      if (validOptions.length < 2) {
        toast({ title: "Erro", description: "Adicione pelo menos 2 opções para múltipla escolha", variant: "destructive" });
        return;
      }
    }

    setSavingQuestion(true);
    try {
      const newQuestion = {
        job_id: jobId!,
        question_text: newQuestionText.trim(),
        question_type: newQuestionType,
        options: newQuestionType === "multipla_escolha" ? newOptions.filter((o) => o.trim()) : null,
        is_required: newIsRequired,
        order_index: questions.length,
      };

      const { data, error } = await supabase
        .from("job_questions")
        .insert(newQuestion)
        .select()
        .single();

      if (error) throw error;

      setQuestions([...questions, data]);
      setNewQuestionText("");
      setNewQuestionType("aberta");
      setNewOptions(["", ""]);
      setNewIsRequired(false);
      toast({ title: "Sucesso", description: "Pergunta adicionada" });
    } catch (error: any) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    try {
      const { error } = await supabase.from("job_questions").delete().eq("id", questionId);
      if (error) throw error;

      const updatedQuestions = questions.filter((q) => q.id !== questionId);
      setQuestions(updatedQuestions);

      updatedQuestions.forEach(async (q, index) => {
        if (q.order_index !== index) {
          await supabase.from("job_questions").update({ order_index: index }).eq("id", q.id);
        }
      });

      toast({ title: "Pergunta removida" });
    } catch (error: any) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    }
  };

  const handleMoveQuestion = async (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= questions.length) return;

    const updatedQuestions = [...questions];
    [updatedQuestions[index], updatedQuestions[newIndex]] = [updatedQuestions[newIndex], updatedQuestions[index]];

    updatedQuestions.forEach((q, i) => (q.order_index = i));
    setQuestions(updatedQuestions);

    try {
      await supabase.from("job_questions").update({ order_index: newIndex }).eq("id", questions[index].id);
      await supabase.from("job_questions").update({ order_index: index }).eq("id", questions[newIndex].id);
    } catch (error) {
      console.error("Error reordering questions:", error);
    }
  };

  const handleAddOption = () => setNewOptions([...newOptions, ""]);
  const handleRemoveOption = (index: number) => {
    if (newOptions.length > 2) {
      setNewOptions(newOptions.filter((_, i) => i !== index));
    }
  };
  const handleOptionChange = (index: number, value: string) => {
    const updated = [...newOptions];
    updated[index] = value;
    setNewOptions(updated);
  };

  const handleToggle = (value: string, currentValues: string[], setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter((prev) => prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);
  };

  const handleAddRequirement = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && newRequirement.trim()) {
      e.preventDefault();
      if (!requirements.includes(newRequirement.trim())) {
        setRequirements([...requirements, newRequirement.trim()]);
      }
      setNewRequirement("");
    }
  };

  const handleAddDesirable = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && newDesirable.trim()) {
      e.preventDefault();
      if (!desirables.includes(newDesirable.trim())) {
        setDesirables([...desirables, newDesirable.trim()]);
      }
      setNewDesirable("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast({ title: "Erro", description: "Informe o título da vaga", variant: "destructive" });
      return;
    }
    if (!description.trim()) {
      toast({ title: "Erro", description: "Informe a descrição da vaga", variant: "destructive" });
      return;
    }
    if (!area) {
      toast({ title: "Erro", description: "Selecione a área/setor", variant: "destructive" });
      return;
    }
    if (jobTypes.length === 0) {
      toast({ title: "Erro", description: "Selecione pelo menos um modelo de trabalho", variant: "destructive" });
      return;
    }
    if (contractTypes.length === 0) {
      toast({ title: "Erro", description: "Selecione pelo menos um regime de contratação", variant: "destructive" });
      return;
    }
    if (showLocationFields && (!state || !city.trim())) {
      toast({ title: "Erro", description: "Informe a cidade e estado para vagas presenciais ou híbridas", variant: "destructive" });
      return;
    }

    setSubmitting(true);

    try {
      const { error } = await supabase
        .from("jobs")
        .update({
          title: title.trim(),
          description: description.trim(),
          job_type: jobTypes,
          area: area,
          state: showLocationFields ? state : null,
          city: showLocationFields ? city.trim() : null,
          neighborhood: showLocationFields && neighborhood.trim() ? neighborhood.trim() : null,
          work_schedule: workSchedules.length > 0 ? workSchedules : null,
          contract_type: contractTypes,
          salary_range_start: !salaryUndefined && salaryMin ? parseCurrency(salaryMin) : null,
          salary_range_end: !salaryUndefined && salaryMax ? parseCurrency(salaryMax) : null,
          benefits: benefits.length > 0 ? benefits : null,
          status: status,
          is_active: status === "ativa",
          is_pcd_eligible: isPcdEligible,
          is_first_job: isFirstJob,
          is_half_period: isHalfPeriod,
          is_home_office: isHomeOffice,
          is_confidential: isUserStartPlan ? false : isConfidential,
          is_featured: canUseFeaturedOption ? isFeatured : existingIsFeatured,
          experience_preference: experiencePreference,
          requirements: requirements.length > 0 ? requirements : null,
          desirables: desirables.length > 0 ? desirables : null,
          education_level: educationLevel || null,
          cnh_required: cnhRequired.length > 0 ? cnhRequired : null,
          other_benefits: benefits.includes("outros") && otherBenefits.trim() ? otherBenefits.trim() : null,
        })
        .eq("id", jobId);

      if (error) throw error;

      toast({ title: "Sucesso!", description: "Vaga atualizada com sucesso." });
      navigate("/empresa/dashboard");
    } catch (error: any) {
      console.error("Error updating job:", error);
      toast({
        title: "Erro ao atualizar vaga",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await supabase.from("applications").delete().eq("job_id", jobId);
      await supabase.from("job_questions").delete().eq("job_id", jobId);
      const { error } = await supabase.from("jobs").delete().eq("id", jobId);

      if (error) throw error;

      toast({ title: "Vaga excluída", description: "A vaga foi removida permanentemente." });
      navigate("/empresa/dashboard");
    } catch (error: any) {
      console.error("Error deleting job:", error);
      toast({
        title: "Erro ao excluir vaga",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  if (accessLoading || loadingJob) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center bg-muted/30">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 flex">
        <EmpresaSidebar />
        <main className="flex-1 p-4 md:p-6 bg-muted/30 overflow-auto">
          <div className="mobile-container max-w-3xl md:mx-auto space-y-6 pt-14 md:pt-0">
            {/* Header */}
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/empresa/dashboard")}
                className="hidden md:flex"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-2xl md:text-3xl font-display font-bold">Editar Vaga</h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Section 1: Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    Dados Básicos da Vaga
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Título do Cargo *</Label>
                    <Input id="title" placeholder="Ex: Desenvolvedor Full Stack" value={title} onChange={(e) => setTitle(e.target.value)} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Descrição das Atividades *</Label>
                    <Textarea id="description" placeholder="Descreva as principais responsabilidades..." rows={5} value={description} onChange={(e) => setDescription(e.target.value)} />
                  </div>

                  <div className="space-y-2">
                    <Label>Área / Setor *</Label>
                    <Select value={area} onValueChange={setArea}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a área" />
                      </SelectTrigger>
                      <SelectContent>
                        {AREAS.map((a) => (
                          <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Section 2: Job Type */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Tipo da Vaga
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="pcd" checked={isPcdEligible} onCheckedChange={(c) => setIsPcdEligible(c as boolean)} />
                      <Label htmlFor="pcd" className="text-sm font-normal cursor-pointer">Vaga para PCD</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="first-job" checked={isFirstJob} onCheckedChange={(c) => setIsFirstJob(c as boolean)} />
                      <Label htmlFor="first-job" className="text-sm font-normal cursor-pointer">1° Emprego</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="half-period" checked={isHalfPeriod} onCheckedChange={(c) => setIsHalfPeriod(c as boolean)} />
                      <Label htmlFor="half-period" className="text-sm font-normal cursor-pointer">Meio Período</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="home-office" checked={isHomeOffice} onCheckedChange={(c) => setIsHomeOffice(c as boolean)} />
                      <Label htmlFor="home-office" className="text-sm font-normal cursor-pointer">Home-office</Label>
                    </div>
                  </div>

                  {!isUserStartPlan && (
                    <div className="border-t border-border pt-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="confidential" className="flex items-center gap-2">
                            <EyeOff className="h-4 w-4" />
                            Vaga Confidencial
                          </Label>
                          <p className="text-sm text-muted-foreground">O nome da empresa não será exibido aos candidatos</p>
                        </div>
                        <Switch id="confidential" checked={isConfidential} onCheckedChange={setIsConfidential} />
                      </div>
                      {isConfidential && (
                        <Alert className="mt-3 bg-yellow-200 border-yellow-300 dark:bg-yellow-700/30 dark:border-yellow-700/50">
                          <AlertTriangle className="h-4 w-4 text-yellow-700 dark:text-yellow-400" />
                          <AlertDescription className="text-yellow-800 dark:text-yellow-200">
                            A vaga será publicada como "Empresa Confidencial"
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}

                  {canUseFeaturedOption && (
                    <div className="border-t border-border pt-4 mt-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="featured" className="flex items-center gap-2">
                            <Star className="h-4 w-4 text-yellow-500" />
                            Vaga em Destaque
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Sua vaga será exibida na seção "Vagas em Destaque" da página inicial
                          </p>
                        </div>
                        <Switch id="featured" checked={isFeatured} onCheckedChange={setIsFeatured} />
                      </div>
                      {isFeatured && (
                        <Alert className="mt-3 bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800/50">
                          <Star className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                          <AlertDescription className="text-yellow-800 dark:text-yellow-200/90">
                            Esta vaga terá maior visibilidade na página inicial
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Section 3: Work Model */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    Modelo de Trabalho
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <Label>Modelo de Trabalho *</Label>
                    <div className="flex flex-wrap gap-4">
                      {WORK_MODELS.map((model) => (
                        <div key={model.value} className="flex items-center space-x-2">
                          <Checkbox id={`workmodel-${model.value}`} checked={jobTypes.includes(model.value)} onCheckedChange={() => handleToggle(model.value, jobTypes, setJobTypes)} />
                          <Label htmlFor={`workmodel-${model.value}`} className="text-sm font-normal cursor-pointer">{model.label}</Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {showLocationFields && (
                    <div className="space-y-4">
                      <StateCitySelector
                        state={state}
                        city={city}
                        onStateChange={(value) => { setState(value); setCity(""); }}
                        onCityChange={setCity}
                        stateLabel="Estado"
                        cityLabel="Cidade"
                        required
                      />
                      <div className="space-y-2">
                        <Label>Bairro / Região</Label>
                        <Input placeholder="Opcional" value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} />
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <Label>Horário de Trabalho</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {WORK_SCHEDULES.map((schedule) => (
                        <div key={schedule.value} className="flex items-center space-x-2">
                          <Checkbox id={`schedule-${schedule.value}`} checked={workSchedules.includes(schedule.value)} onCheckedChange={() => handleToggle(schedule.value, workSchedules, setWorkSchedules)} />
                          <Label htmlFor={`schedule-${schedule.value}`} className="text-sm font-normal cursor-pointer">{schedule.label}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Section 4: Candidate Requirements */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-primary" />
                    Requisitos do Candidato
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Experience preference */}
                  <div className="space-y-3">
                    <Label>Experiência</Label>
                    <RadioGroup value={experiencePreference} onValueChange={setExperiencePreference} className="flex flex-col gap-2">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="com_experiencia" id="exp-com" />
                        <Label htmlFor="exp-com" className="font-normal cursor-pointer">Desejo candidato com experiência</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="sem_experiencia" id="exp-sem" />
                        <Label htmlFor="exp-sem" className="font-normal cursor-pointer">Desejo candidato sem experiência</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="indiferente" id="exp-ind" />
                        <Label htmlFor="exp-ind" className="font-normal cursor-pointer">Indiferente</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Education level */}
                  <div className="space-y-2">
                    <Label>Escolaridade Mínima</Label>
                    <Select value={educationLevel} onValueChange={setEducationLevel}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a escolaridade" />
                      </SelectTrigger>
                      <SelectContent>
                        {EDUCATION_LEVELS.map((level) => (
                          <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* CNH */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Car className="h-4 w-4" />
                      Carteira de Habilitação (CNH)
                    </Label>
                    <div className="flex flex-wrap gap-4">
                      {CNH_TYPES.map((cnh) => (
                        <div key={cnh.value} className="flex items-center space-x-2">
                          <Checkbox id={`cnh-${cnh.value}`} checked={cnhRequired.includes(cnh.value)} onCheckedChange={() => handleToggle(cnh.value, cnhRequired, setCnhRequired)} />
                          <Label htmlFor={`cnh-${cnh.value}`} className="text-sm font-normal cursor-pointer">{cnh.label}</Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Requirements (tags) */}
                  <div className="space-y-3">
                    <Label>Requisitos da vaga</Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {requirements.map((req) => (
                        <Badge key={req} variant="secondary" className="gap-1 pr-1">
                          {req}
                          <button type="button" onClick={() => setRequirements(requirements.filter((r) => r !== req))} className="ml-1 hover:bg-muted rounded">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <Input
                      placeholder="Digite um requisito e pressione Enter"
                      value={newRequirement}
                      onChange={(e) => setNewRequirement(e.target.value)}
                      onKeyDown={handleAddRequirement}
                    />
                  </div>

                  {/* Desirables (tags) */}
                  <div className="space-y-3">
                    <Label>Desejáveis (opcional)</Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {desirables.map((des) => (
                        <Badge key={des} variant="outline" className="gap-1 pr-1">
                          {des}
                          <button type="button" onClick={() => setDesirables(desirables.filter((d) => d !== des))} className="ml-1 hover:bg-muted rounded">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <Input
                      placeholder="Digite um desejável e pressione Enter"
                      value={newDesirable}
                      onChange={(e) => setNewDesirable(e.target.value)}
                      onKeyDown={handleAddDesirable}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Section 5: Contract Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-primary" />
                    Informações Contratuais
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <Label>Regime de Contratação *</Label>
                    <div className="flex flex-wrap gap-4">
                      {CONTRACT_TYPES.map((type) => (
                        <div key={type.value} className="flex items-center space-x-2">
                          <Checkbox id={`contract-${type.value}`} checked={contractTypes.includes(type.value)} onCheckedChange={() => handleToggle(type.value, contractTypes, setContractTypes)} />
                          <Label htmlFor={`contract-${type.value}`} className="text-sm font-normal cursor-pointer">{type.label}</Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="salary-undefined" checked={salaryUndefined} onCheckedChange={(checked) => { setSalaryUndefined(checked as boolean); if (checked) { setSalaryMin(""); setSalaryMax(""); } }} />
                      <Label htmlFor="salary-undefined" className="text-sm font-normal cursor-pointer">Salário a definir</Label>
                    </div>
                    {!salaryUndefined && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Salário Mínimo (R$)</Label>
                          <Input type="text" placeholder="R$ 3.000,00" value={salaryMin} onChange={(e) => setSalaryMin(handleCurrencyInput(e.target.value))} />
                        </div>
                        <div className="space-y-2">
                          <Label>Salário Máximo (R$)</Label>
                          <Input type="text" placeholder="R$ 5.000,00" value={salaryMax} onChange={(e) => setSalaryMax(handleCurrencyInput(e.target.value))} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label>Benefícios</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {BENEFITS_OPTIONS.map((benefit) => (
                        <div key={benefit.id} className="flex items-center space-x-2">
                          <Checkbox id={`benefit-${benefit.id}`} checked={benefits.includes(benefit.id)} onCheckedChange={() => handleToggle(benefit.id, benefits, setBenefits)} />
                          <Label htmlFor={`benefit-${benefit.id}`} className="text-sm font-normal cursor-pointer">{benefit.label}</Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {benefits.includes("outros") && (
                    <div className="space-y-2">
                      <Label>Descreva os outros benefícios</Label>
                      <Textarea
                        placeholder="Ex: Auxílio creche, seguro de vida, etc."
                        value={otherBenefits}
                        onChange={(e) => setOtherBenefits(e.target.value)}
                        rows={3}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Section 6: Custom Questions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-primary" />
                    Perguntas Personalizadas
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Perguntas que os candidatos deverão responder ao se candidatar.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingQuestions ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : questions.length > 0 ? (
                    <div className="space-y-3">
                      {questions.map((q, index) => (
                        <div key={q.id} className="flex items-start gap-3 p-3 border rounded-lg bg-muted/30">
                          <div className="flex flex-col gap-1">
                            <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleMoveQuestion(index, "up")} disabled={index === 0}>
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleMoveQuestion(index, "down")} disabled={index === questions.length - 1}>
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{q.question_text}</p>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs">
                                {QUESTION_TYPES.find((t) => t.value === q.question_type)?.label || q.question_type}
                              </Badge>
                              {q.is_required && (
                                <Badge variant="outline" className="text-xs">Obrigatória</Badge>
                              )}
                            </div>
                            {q.question_type === "multipla_escolha" && q.options && (
                              <div className="mt-2 text-sm text-muted-foreground">
                                Opções: {q.options.join(", ")}
                              </div>
                            )}
                          </div>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button type="button" variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                                <X className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Excluir pergunta?</AlertDialogTitle>
                                <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteQuestion(q.id)}>Excluir</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">Nenhuma pergunta adicionada ainda.</p>
                  )}

                  <div className="border-t border-border pt-4 mt-4 space-y-4">
                    <h4 className="font-medium text-sm">Adicionar nova pergunta</h4>
                    <Textarea placeholder="Digite o texto da pergunta..." value={newQuestionText} onChange={(e) => setNewQuestionText(e.target.value)} rows={2} />
                    <div className="flex flex-wrap gap-4 items-center">
                      <Select value={newQuestionType} onValueChange={setNewQuestionType}>
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {QUESTION_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="new-question-required" checked={newIsRequired} onCheckedChange={(checked) => setNewIsRequired(checked as boolean)} />
                        <Label htmlFor="new-question-required" className="text-sm font-normal cursor-pointer">Obrigatória</Label>
                      </div>
                    </div>

                    {newQuestionType === "multipla_escolha" && (
                      <div className="space-y-2">
                        <Label className="text-sm">Opções de resposta</Label>
                        {newOptions.map((option, index) => (
                          <div key={index} className="flex gap-2">
                            <Input placeholder={`Opção ${index + 1}`} value={option} onChange={(e) => handleOptionChange(index, e.target.value)} />
                            {newOptions.length > 2 && (
                              <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveOption(index)}>
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={handleAddOption}>
                          <Plus className="h-4 w-4 mr-1" /> Adicionar opção
                        </Button>
                      </div>
                    )}

                    <Button type="button" variant="secondary" onClick={handleAddQuestion} disabled={savingQuestion}>
                      {savingQuestion ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                      Adicionar Pergunta
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Anti-discrimination warning */}
              <Alert className="bg-yellow-200 border-yellow-300 dark:bg-yellow-700/30 dark:border-yellow-700/50">
                <AlertTriangle className="h-4 w-4 text-yellow-700 dark:text-yellow-400" />
                <AlertDescription className="text-yellow-800 dark:text-yellow-200">
                  <strong>Aviso Legal:</strong> De acordo com a Constituição Federal Brasileira (Art. 5º) e a CLT (Art. 373-A), 
                  é proibida qualquer forma de discriminação no processo seletivo por motivo de sexo, origem, raça, cor, 
                  estado civil, situação familiar, deficiência, reabilitação profissional, idade, entre outros.
                </AlertDescription>
              </Alert>

              {/* Actions */}
              <div className="flex flex-col-reverse sm:flex-row gap-4 sm:justify-between">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="destructive" disabled={deleting}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Excluir Vaga Permanentemente
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Excluir vaga?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta ação não pode ser desfeita. Todas as candidaturas e perguntas serão removidas.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <div className="flex flex-col-reverse sm:flex-row gap-4 w-full sm:w-auto">
                  <Button type="button" variant="outline" onClick={() => navigate("/empresa/dashboard")} disabled={submitting}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Salvar Alterações
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default EmpresaEditarVagaPage;
